import { useState } from 'react'
import { useDiffStatus } from '../../hooks/useSharing'
import toast from 'react-hot-toast'
import clsx from 'clsx'

/**
 * DiffStatusBanner
 *
 * Shows inline on a document/spec/test-plan detail page to indicate
 * whether the artifact is up to date and offer smart regeneration.
 *
 * Props:
 *   type       — 'document' | 'specification' | 'test-plan'
 *   id         — artifact id
 *   onRegenQueued — optional callback when a regen is successfully queued
 */
export default function DiffStatusBanner({ type, id, onRegenQueued }) {
  const { status, loading, check, regenerate, regenLoading } = useDiffStatus(type, id)
  const [showFiles, setShowFiles] = useState(false)
  const [modeOverride, setModeOverride] = useState('auto')

  const handleRegen = async (mode) => {
    const result = await regenerate(mode)
    if (result?.queued) {
      toast.success(result.message ?? 'Regeneration queued.')
      onRegenQueued?.()
    } else {
      toast(result?.message ?? 'Nothing to regenerate.', { icon: 'ℹ️' })
    }
  }

  if (loading) return (
    <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/3 border border-white/8 text-xs text-white/30 animate-pulse">
      <div className="w-3 h-3 rounded-full bg-white/10" />
      Checking for updates…
    </div>
  )

  if (!status) return null

  // ── Up to date ────────────────────────────────────────────────────────────
  if (status.is_up_to_date) {
    return (
      <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-emerald-500/8 border border-emerald-500/15">
        <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]" />
        <span className="text-xs text-emerald-400 font-medium">Up to date</span>
        {status.head_sha_short && (
          <code className="text-[11px] text-emerald-400/50 font-mono ml-1">{status.head_sha_short}</code>
        )}
      </div>
    )
  }

  // ── Never generated ────────────────────────────────────────────────────────
  if (!status.has_base) {
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl bg-purple-500/8 border border-purple-500/20">
        <div className="flex items-center gap-2.5">
          <svg className="w-4 h-4 text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/>
          </svg>
          <div>
            <p className="text-sm font-semibold text-white">Not yet generated</p>
            <p className="text-xs text-white/40">Run a full generation to create the first version.</p>
          </div>
        </div>
        <button
          disabled={regenLoading}
          onClick={() => handleRegen('full')}
          className="btn-specze-sm text-white flex-shrink-0"
        >
          {regenLoading ? 'Queuing…' : 'Generate Now'}
        </button>
      </div>
    )
  }

  // ── Behind ────────────────────────────────────────────────────────────────
  const recommendation = status.recommendation
  const isDiff         = recommendation === 'diff_regen'
  const isSkip         = recommendation === 'skip'

  const bannerColor = isSkip
    ? 'bg-white/3 border-white/8'
    : isDiff
      ? 'bg-blue-500/8 border-blue-500/20'
      : 'bg-amber-500/8 border-amber-500/20'

  const iconColor = isSkip ? 'text-white/25' : isDiff ? 'text-blue-400' : 'text-amber-400'

  return (
    <div className={clsx('rounded-xl border', bannerColor)}>
      {/* Main row */}
      <div className="flex items-center justify-between gap-3 px-4 py-3.5">
        <div className="flex items-center gap-3 min-w-0">
          <svg className={clsx('w-4 h-4 flex-shrink-0', iconColor)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d={isDiff
                ? "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                : "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              }
            />
          </svg>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-white">
                {isDiff ? 'Updates available' : isSkip ? 'Only non-code files changed' : 'Significant changes detected'}
              </span>
              {/* Commit range */}
              {status.base_sha_short && status.head_sha_short && (
                <span className="text-[11px] font-mono text-white/30">
                  {status.base_sha_short} → {status.head_sha_short}
                </span>
              )}
            </div>
            <p className="text-xs text-white/40 mt-0.5">{status.reason}</p>
          </div>
        </div>

        {/* Actions */}
        {!isSkip && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Diff regen */}
            {isDiff && (
              <button
                disabled={regenLoading}
                onClick={() => handleRegen('diff')}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-blue-500/15 border border-blue-500/30 text-blue-300 text-xs font-semibold hover:bg-blue-500/25 transition-all disabled:opacity-50"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                {regenLoading ? 'Queuing…' : 'Update Changes'}
              </button>
            )}

            {/* Full regen */}
            <button
              disabled={regenLoading}
              onClick={() => handleRegen('full')}
              className={clsx(
                'px-3.5 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50',
                isDiff
                  ? 'bg-white/6 border border-white/12 text-white/60 hover:bg-white/10 hover:text-white'
                  : 'btn-specze-sm text-white'
              )}
            >
              {regenLoading ? 'Queuing…' : isDiff ? 'Full Regen' : 'Regenerate Now'}
            </button>
          </div>
        )}
      </div>

      {/* Changed files toggle */}
      {status.changed_files?.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setShowFiles(!showFiles)}
            className="w-full flex items-center justify-between px-4 py-2 border-t border-white/6 text-xs text-white/30 hover:text-white/50 transition-colors"
          >
            <span>
              {status.files_changed} file{status.files_changed !== 1 ? 's' : ''} changed
              {' '}({status.relevant_files} relevant)
              {status.additions > 0 && <span className="text-emerald-400/60 ml-2">+{status.additions}</span>}
              {status.deletions > 0 && <span className="text-red-400/60 ml-1">-{status.deletions}</span>}
            </span>
            <svg className={clsx('w-3.5 h-3.5 transition-transform', showFiles && 'rotate-180')}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          {showFiles && (
            <div className="px-4 pb-3 space-y-1 animate-fade-in">
              {status.changed_files.map(f => (
                <div key={f.path} className="flex items-center gap-2.5 py-1">
                  <span className={clsx(
                    'text-[10px] font-bold w-14 text-center rounded px-1 py-0.5',
                    f.status === 'added'    && 'text-emerald-400 bg-emerald-500/10',
                    f.status === 'removed'  && 'text-red-400 bg-red-500/10',
                    f.status === 'modified' && 'text-blue-400 bg-blue-500/10',
                    f.status === 'renamed'  && 'text-amber-400 bg-amber-500/10',
                  )}>
                    {f.status}
                  </span>
                  <code className="text-[11px] text-white/50 font-mono truncate flex-1">{f.path}</code>
                  <span className="text-[11px] font-mono text-white/25 flex-shrink-0">
                    {f.additions > 0 && <span className="text-emerald-500/60">+{f.additions}</span>}
                    {f.deletions > 0 && <span className="text-red-500/60 ml-0.5">-{f.deletions}</span>}
                  </span>
                </div>
              ))}
              {status.files_changed > status.changed_files.length && (
                <p className="text-[11px] text-white/25 pt-1">
                  …and {status.files_changed - status.changed_files.length} more files
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
